import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FloatingSidebarButton from '../../components/FloatingSidebarButton/FloatingSidebarButton';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext'; // ⬅️ add
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabase/supabase';
import './Chat.css';

const Chat = () => {
  const navigate = useNavigate();
  const { translations: t } = useLanguage(); // ⬅️ add
  const { currentUser, userProfile } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [oldestMessageCursor, setOldestMessageCursor] = useState(null);
  const [error, setError] = useState(null);

  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const PAGE_SIZE = 50;

  const messagesContainerRef = useRef(null);
  const messageEndRef = useRef(null);
  const realtimeCleanupRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const forceScrollToBottomRef = useRef(false);
  const conversationIdSetRef = useRef(new Set());
  const convRefreshTimerRef = useRef(null);
  const conversationsRef = useRef([]);
  const unreadCacheRef = useRef(new Map()); // convId -> unreadCount

  const safeName = useCallback((profile) => {
    return profile?.display_name || profile?.username || t?.common?.user || 'User';
  }, [t]);

  const resolveAvatarUrl = useCallback((avatarUrl, userId) => {
    if (!avatarUrl) return null;
    if (typeof avatarUrl !== 'string') return null;
    if (/^(http|https):\/\//i.test(avatarUrl)) return avatarUrl;
    if (avatarUrl.startsWith('data:')) return avatarUrl;

    // app-wide convention: storage bucket 'avatars' with path: <userId>/<filename>
    try {
      const { data } = supabase.storage.from('avatars').getPublicUrl(`${userId}/${avatarUrl}`);
      return data?.publicUrl || null;
    } catch (_) {
      return null;
    }
  }, []);

  const fetchProfilesByUserIds = useCallback(async (userIds) => {
    const uniq = [...new Set((userIds || []).filter(Boolean))];
    if (uniq.length === 0) return [];

    const { data, error: qErr } = await supabase
      .from('profiles')
      .select('user_id, username, display_name, avatar_url')
      .in('user_id', uniq);

    if (qErr) throw qErr;
    return data || [];
  }, []);

  const getUnreadCount = useCallback(async (conversationId) => {
    if (!currentUser?.id || !conversationId) return 0;

    // messages not sent by current user
    const { data: allMessages, error: mErr } = await supabase
      .from('messages')
      .select('id')
      .eq('conversation_id', conversationId)
      .neq('sender_id', currentUser.id);

    if (mErr) throw mErr;
    if (!allMessages?.length) return 0;

    const messageIds = allMessages.map((m) => m.id);

    const { data: readMessages, error: rErr } = await supabase
      .from('message_reads')
      .select('message_id')
      .eq('user_id', currentUser.id)
      .in('message_id', messageIds);

    if (rErr) throw rErr;

    const readSet = new Set((readMessages || []).map((r) => r.message_id));
    return allMessages.filter((m) => !readSet.has(m.id)).length;
  }, [currentUser?.id]);

  const markMessagesAsRead = useCallback(async (conversationId) => {
    if (!currentUser?.id || !conversationId) return;

    const { data: unreadMessages, error: mErr } = await supabase
      .from('messages')
      .select('id')
      .eq('conversation_id', conversationId)
      .neq('sender_id', currentUser.id);

    if (mErr) throw mErr;
    if (!unreadMessages?.length) return;

    const messageIds = unreadMessages.map((m) => m.id);

    const { data: readMessages, error: rErr } = await supabase
      .from('message_reads')
      .select('message_id')
      .eq('user_id', currentUser.id)
      .in('message_id', messageIds);

    if (rErr) throw rErr;

    const alreadyReadIds = new Set((readMessages || []).map((r) => r.message_id));
    const toInsert = messageIds
      .filter((id) => !alreadyReadIds.has(id))
      .map((id) => ({ message_id: id, user_id: currentUser.id }));

    if (toInsert.length === 0) {
      // still notify global UI that this conversation is considered "read"
      try {
        window.dispatchEvent(new CustomEvent('chat:read', { detail: { conversationId } }));
      } catch (_) {}
      return;
    }

    const { error: iErr } = await supabase.from('message_reads').insert(toInsert);
    if (iErr) throw iErr;

    // Notify global UI (e.g. FloatingSidebarButton chat badge) that this conversation was read
    try {
      window.dispatchEvent(new CustomEvent('chat:read', { detail: { conversationId } }));
    } catch (_) {}
  }, [currentUser?.id]);

  const fetchConversations = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoadingConversations(true);
    setError(null);

    try {
      // Step 1: conversation IDs where user participates
      const { data: userConvs, error: pErr } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', currentUser.id);

      if (pErr) throw pErr;
      const conversationIds = (userConvs || []).map((c) => c.conversation_id);
      if (conversationIds.length === 0) {
        setConversations([]);
        return;
      }

      // Step 2: conversations
      const { data: convs, error: cErr } = await supabase
        .from('conversations')
        .select('id, type, name, created_at, updated_at')
        .in('id', conversationIds)
        .order('updated_at', { ascending: false });

      if (cErr) throw cErr;

      // Step 3: participants
      const { data: allParticipants, error: aErr } = await supabase
        .from('conversation_participants')
        .select('conversation_id, user_id')
        .in('conversation_id', conversationIds);

      if (aErr) throw aErr;

      const participantUserIds = [...new Set((allParticipants || []).map((p) => p.user_id))];
      const profiles = await fetchProfilesByUserIds(participantUserIds);
      const profileMap = new Map(profiles.map((p) => [p.user_id, p]));

      // Step 4: last messages (batch fetch, then pick first per conversation)
      const { data: recentMessages, error: lmErr } = await supabase
        .from('messages')
        .select('id, conversation_id, sender_id, content, attachment_type, created_at')
        .in('conversation_id', conversationIds)
        .order('created_at', { ascending: false })
        .limit(120);

      if (lmErr) throw lmErr;
      const lastMessageByConv = new Map();
      for (const msg of recentMessages || []) {
        if (!lastMessageByConv.has(msg.conversation_id)) {
          lastMessageByConv.set(msg.conversation_id, msg);
        }
      }

      // Step 5: unread counts (performance)
      // Prefer cached/unread state from the last known conversations; only compute if unknown.
      const prev = conversationsRef.current || [];
      const prevMap = new Map(prev.map((c) => [c.id, c]));

      const toCompute = (convs || [])
        .map((c) => c.id)
        .filter((id) => {
          if (!id) return false;
          if (unreadCacheRef.current.has(id)) return false;
          const prevC = prevMap.get(id);
          return typeof prevC?.unreadCount !== 'number';
        });

      if (toCompute.length > 0) {
        const computed = await Promise.all(
          toCompute.map(async (id) => {
            try {
              const count = await getUnreadCount(id);
              return [id, count];
            } catch (_) {
              return [id, 0];
            }
          })
        );
        for (const [id, count] of computed) unreadCacheRef.current.set(id, count);
      }

      const built = (convs || []).map((conv) => {
        const participantIds = (allParticipants || [])
          .filter((p) => p.conversation_id === conv.id)
          .map((p) => p.user_id);

        const participants = participantIds
          .map((uid) => profileMap.get(uid) || { user_id: uid, display_name: null, username: null, avatar_url: null })
          .map((p) => ({
            ...p,
            avatar_url: resolveAvatarUrl(p.avatar_url, p.user_id),
          }));

        const other = participants.find((p) => p.user_id !== currentUser.id) || null;
        const prevC = prevMap.get(conv.id);
        const cachedUnread =
          unreadCacheRef.current.has(conv.id)
            ? unreadCacheRef.current.get(conv.id)
            : (typeof prevC?.unreadCount === 'number' ? prevC.unreadCount : 0);

        return {
          ...conv,
          participantIds,
          participants,
          otherParticipant: other,
          lastMessage: lastMessageByConv.get(conv.id) || null,
          unreadCount: cachedUnread || 0,
        };
      });

      setConversations(built);
    } catch (e) {
      console.error('[Chat] fetchConversations error:', e);
      setError(e?.message || 'Failed to load conversations');
    } finally {
      setLoadingConversations(false);
    }
  }, [currentUser?.id, fetchProfilesByUserIds, getUnreadCount, resolveAvatarUrl]);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!currentUser?.id || !conversationId) return;
    setLoadingMessages(true);
    setError(null);

    try {
      const { data: msgs, error: mErr } = await supabase
        .from('messages')
        .select('id, conversation_id, sender_id, content, attachment_url, attachment_type, created_at, updated_at, status')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      if (mErr) throw mErr;

      const latestAsc = [...(msgs || [])].reverse();
      const senderIds = [...new Set(latestAsc.map((m) => m.sender_id).filter(Boolean))];
      const senderProfiles = await fetchProfilesByUserIds(senderIds);
      const senderMap = new Map(senderProfiles.map((p) => [p.user_id, p]));

      const built = latestAsc.map((m) => ({
        ...m,
        sender: m.sender_id ? (senderMap.get(m.sender_id) || null) : null,
      }));

      setMessages(built);
      setHasMoreMessages((msgs || []).length === PAGE_SIZE);
      setOldestMessageCursor(built[0]?.created_at || null);
      await markMessagesAsRead(conversationId);

      // refresh unread badge in sidebar for this conversation
      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
      );

      // initial load should scroll to bottom
      forceScrollToBottomRef.current = true;
    } catch (e) {
      console.error('[Chat] fetchMessages error:', e);
      setError(e?.message || 'Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [PAGE_SIZE, currentUser?.id, fetchProfilesByUserIds, markMessagesAsRead]);

  const loadOlderMessages = useCallback(async () => {
    if (!currentUser?.id || !selectedConversationId) return;
    if (loadingOlder || !hasMoreMessages || !oldestMessageCursor) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    setLoadingOlder(true);
    try {
      const prevScrollHeight = container.scrollHeight;
      const prevScrollTop = container.scrollTop;

      const { data: older, error: oErr } = await supabase
        .from('messages')
        .select('id, conversation_id, sender_id, content, attachment_url, attachment_type, created_at, updated_at, status')
        .eq('conversation_id', selectedConversationId)
        .lt('created_at', oldestMessageCursor)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      if (oErr) throw oErr;
      const olderAsc = [...(older || [])].reverse();
      if (olderAsc.length === 0) {
        setHasMoreMessages(false);
        return;
      }

      const senderIds = [...new Set(olderAsc.map((m) => m.sender_id).filter(Boolean))];
      const senderProfiles = await fetchProfilesByUserIds(senderIds);
      const senderMap = new Map(senderProfiles.map((p) => [p.user_id, p]));

      const built = olderAsc.map((m) => ({
        ...m,
        sender: m.sender_id ? (senderMap.get(m.sender_id) || null) : null,
      }));

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const deduped = built.filter((m) => !existingIds.has(m.id));
        return [...deduped, ...prev];
      });

      setHasMoreMessages((older || []).length === PAGE_SIZE);
      setOldestMessageCursor(built[0]?.created_at || oldestMessageCursor);

      // preserve scroll position after prepending
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
      });
    } catch (e) {
      console.error('[Chat] loadOlderMessages error:', e);
    } finally {
      setLoadingOlder(false);
    }
  }, [PAGE_SIZE, currentUser?.id, fetchProfilesByUserIds, hasMoreMessages, loadingOlder, oldestMessageCursor, selectedConversationId]);

  const subscribeToMessages = useCallback((conversationId) => {
    if (!currentUser?.id || !conversationId) return () => {};

    // cleanup old subscription
    try {
      realtimeCleanupRef.current?.();
    } catch (_) {}
    realtimeCleanupRef.current = null;

    activeConversationIdRef.current = conversationId;

    const channel = supabase
      .channel(`messages_${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          const msg = payload.new;
          // ignore if user already navigated away
          if (activeConversationIdRef.current !== conversationId) return;

          // Prevent "double send": our UI already adds optimistic + replaces on success.
          // Supabase realtime will also emit our own INSERT, so ignore it.
          if (msg?.sender_id === currentUser.id) return;

          let sender = null;
          if (msg?.sender_id) {
            try {
              const { data } = await supabase
                .from('profiles')
                .select('user_id, username, display_name, avatar_url')
                .eq('user_id', msg.sender_id)
                .maybeSingle();
              sender = data || null;
            } catch (_) {
              sender = null;
            }
          }

          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, { ...msg, sender }];
          });

          // If we're viewing the conversation and it's from the other user, mark read
          if (msg?.sender_id && msg.sender_id !== currentUser.id) {
            try {
              await markMessagesAsRead(conversationId);
              setConversations((prev) =>
                prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
              );
            } catch (_) {}
          }

          // update last message preview
          setConversations((prev) =>
            prev.map((c) => (c.id === conversationId ? { ...c, lastMessage: msg } : c))
          );
        }
      )
      .subscribe();

    const cleanup = () => {
      try {
        supabase.removeChannel(channel);
      } catch (_) {}
    };

    realtimeCleanupRef.current = cleanup;
    return cleanup;
  }, [currentUser?.id, markMessagesAsRead]);

  const createOrGetPrivateConversation = useCallback(async (otherUserId) => {
    if (!currentUser?.id || !otherUserId) return null;

    // Step 1: current user's conversation ids
    const { data: userConvs, error: uErr } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', currentUser.id);

    if (uErr) throw uErr;
    const conversationIds = (userConvs || []).map((c) => c.conversation_id);

    if (conversationIds.length > 0) {
      // Step 2: participants for these conversations
      const { data: participants, error: pErr } = await supabase
        .from('conversation_participants')
        .select('conversation_id, user_id')
        .in('conversation_id', conversationIds);

      if (pErr) throw pErr;

      const grouped = new Map();
      for (const p of participants || []) {
        const arr = grouped.get(p.conversation_id) || [];
        arr.push(p.user_id);
        grouped.set(p.conversation_id, arr);
      }

      const existingId = [...grouped.entries()].find(([_, ids]) => {
        const uniq = [...new Set(ids)];
        return uniq.length === 2 && uniq.includes(currentUser.id) && uniq.includes(otherUserId);
      })?.[0];

      if (existingId) {
        const { data: conv, error: cErr } = await supabase
          .from('conversations')
          .select('id, type, name, created_at, updated_at')
          .eq('id', existingId)
          .eq('type', 'private')
          .maybeSingle();

        if (cErr) throw cErr;
        if (conv) return conv;
      }
    }

    // Step 3: create new conversation
    const { data: newConv, error: nErr } = await supabase
      .from('conversations')
      .insert({ type: 'private', name: null })
      .select('id, type, name, created_at, updated_at')
      .single();

    if (nErr) throw nErr;

    const { error: iErr } = await supabase.from('conversation_participants').insert([
      { conversation_id: newConv.id, user_id: currentUser.id, role: 'member' },
      { conversation_id: newConv.id, user_id: otherUserId, role: 'member' },
    ]);

    if (iErr) throw iErr;
    return newConv;
  }, [currentUser?.id]);

  const onSelectConversation = useCallback(async (conversationId) => {
    setSelectedConversationId(conversationId);
  }, []);

  const selectedConversation = useMemo(() => {
    return conversations.find((c) => c.id === selectedConversationId) || null;
  }, [conversations, selectedConversationId]);

  const selectedOther = selectedConversation?.otherParticipant || null;

  const sendMessage = useCallback(async () => {
    if (!currentUser?.id || !selectedConversationId) return;
    const trimmed = messageText.trim();
    if (!trimmed) return;

    const tempId = `temp_${Date.now()}`;
    const optimistic = {
      id: tempId,
      conversation_id: selectedConversationId,
      sender_id: currentUser.id,
      content: trimmed,
      attachment_url: null,
      attachment_type: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'sent',
      sender: { user_id: currentUser.id, display_name: userProfile?.display_name, username: userProfile?.username, avatar_url: userProfile?.avatar_url },
    };

    setMessages((prev) => [...prev, optimistic]);
    setMessageText('');
    forceScrollToBottomRef.current = true;

    try {
      const { data: msg, error: mErr } = await supabase
        .from('messages')
        .insert({
          conversation_id: selectedConversationId,
          sender_id: currentUser.id,
          content: trimmed,
          attachment_url: null,
          attachment_type: null,
          status: 'sent',
        })
        .select('id, conversation_id, sender_id, content, attachment_url, attachment_type, created_at, updated_at, status')
        .single();

      if (mErr) throw mErr;

      await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', selectedConversationId);

      // mark sender read (so it doesn't show as unread for the sender)
      await supabase.from('message_reads').insert({ message_id: msg.id, user_id: currentUser.id });

      // replace optimistic with real
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m.id !== tempId);
        const withoutRealDupes = withoutTemp.filter((m) => m.id !== msg.id);
        return [...withoutRealDupes, { ...msg, sender: optimistic.sender }];
      });
      setConversations((prev) => prev.map((c) => (c.id === selectedConversationId ? { ...c, lastMessage: msg } : c)));
    } catch (e) {
      console.error('[Chat] sendMessage error:', e);
      setError(e?.message || 'Failed to send message');
      // rollback optimistic message
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setMessageText(trimmed);
    }
  }, [currentUser?.id, messageText, selectedConversationId, userProfile?.display_name, userProfile?.username, userProfile?.avatar_url]);

  // initial load
  useEffect(() => {
    if (!currentUser?.id) return;
    fetchConversations();
  }, [currentUser?.id, fetchConversations]);

  // Keep refs in sync for performance (avoids re-fetch triggers)
  useEffect(() => {
    conversationsRef.current = conversations || [];
  }, [conversations]);

  // Keep a fast lookup set for realtime updates
  useEffect(() => {
    conversationIdSetRef.current = new Set((conversations || []).map((c) => c.id));
  }, [conversations]);

  // Realtime: update conversation list instantly when any new message arrives
  useEffect(() => {
    if (!currentUser?.id) return;

    const scheduleConversationsRefresh = () => {
      if (convRefreshTimerRef.current) return;
      convRefreshTimerRef.current = setTimeout(() => {
        convRefreshTimerRef.current = null;
        fetchConversations();
      }, 500);
    };

    const channel = supabase
      .channel(`chat_list_${currentUser.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload) => {
          const msg = payload?.new;
          if (!msg?.conversation_id) return;

          const convId = msg.conversation_id;

          // If we don't know about this conversation yet, refresh list (someone started a new chat)
          if (!conversationIdSetRef.current.has(convId)) {
            scheduleConversationsRefresh();
            return;
          }

          // If currently open conversation (and not from me), ensure it appears ASAP even if the per-conv subscription lags
          if (selectedConversationId && convId === selectedConversationId && msg.sender_id !== currentUser.id) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, { ...msg, sender: null }];
            });
            try { await markMessagesAsRead(convId); } catch (_) {}
          }

          // Update list preview + unread counts without refetching
          setConversations((prev) => {
            const idx = prev.findIndex((c) => c.id === convId);
            if (idx === -1) return prev;

            const current = prev[idx];
            const isActive = selectedConversationId && convId === selectedConversationId;
            const isIncoming = msg.sender_id && msg.sender_id !== currentUser.id;

            const nextUnread = isIncoming && !isActive ? (current.unreadCount || 0) + 1 : (isActive ? 0 : current.unreadCount || 0);
            // keep cache in sync so we don't recompute on refresh
            try { unreadCacheRef.current.set(convId, nextUnread); } catch (_) {}
            const updated = { ...current, lastMessage: msg, unreadCount: nextUnread };

            // Move updated conversation to top (WhatsApp-like)
            const next = [...prev];
            next.splice(idx, 1);
            next.unshift(updated);
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch (_) {}
      try {
        if (convRefreshTimerRef.current) clearTimeout(convRefreshTimerRef.current);
      } catch (_) {}
      convRefreshTimerRef.current = null;
    };
  }, [currentUser?.id, fetchConversations, markMessagesAsRead, selectedConversationId]);

  // load messages + subscribe when conversation changes
  useEffect(() => {
    if (!currentUser?.id || !selectedConversationId) return;
    fetchMessages(selectedConversationId);
    const cleanup = subscribeToMessages(selectedConversationId);
    return () => cleanup?.();
  }, [currentUser?.id, selectedConversationId, fetchMessages, subscribeToMessages]);

  // Expose currently open conversation for global badge logic
  useEffect(() => {
    try {
      if (selectedConversationId) {
        localStorage.setItem('active_chat_conversation_id', selectedConversationId);
      } else {
        localStorage.removeItem('active_chat_conversation_id');
      }
    } catch (_) {}

    // On page exit/unmount, clear it so badge logic becomes consistent outside /chat
    return () => {
      try { localStorage.removeItem('active_chat_conversation_id'); } catch (_) {}
    };
  }, [selectedConversationId]);

  // Auto-scroll only if user is near the bottom OR we explicitly forced it (e.g. after sending)
  useEffect(() => {
    const shouldScroll = isNearBottomRef.current || forceScrollToBottomRef.current;
    if (!shouldScroll) return;

    forceScrollToBottomRef.current = false;
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const onMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 140;

    // Load older messages when scrolled near top
    if (el.scrollTop < 60) {
      loadOlderMessages();
    }
  }, [loadOlderMessages]);

  // user search (debounced-ish)
  useEffect(() => {
    if (!currentUser?.id) return;
    const q = userQuery.trim();
    if (!q) {
      setUserResults([]);
      return;
    }

    const handle = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const { data, error: sErr } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url')
          .or(`username.ilike.%${q}%,display_name.ilike.%${q}%`)
          .neq('user_id', currentUser.id)
          .limit(20);

        if (sErr) throw sErr;
        const mapped = (data || []).map((p) => ({
          ...p,
          avatar_url: resolveAvatarUrl(p.avatar_url, p.user_id),
        }));
        setUserResults(mapped);
      } catch (e) {
        console.error('[Chat] searchUsers error:', e);
        setUserResults([]);
      } finally {
        setSearchingUsers(false);
      }
    }, 350);

    return () => clearTimeout(handle);
  }, [currentUser?.id, resolveAvatarUrl, userQuery]);

  const startChatWithUser = useCallback(async (otherUserId) => {
    setError(null);
    try {
      const conv = await createOrGetPrivateConversation(otherUserId);
      await fetchConversations();
      setSelectedConversationId(conv.id);
      setUserQuery('');
      setUserResults([]);

      // Notify global UI to refresh conversation ids / unread badges
      try {
        window.dispatchEvent(new CustomEvent('chat:conversationCreated', { detail: { conversationId: conv.id } }));
      } catch (_) {}
    } catch (e) {
      console.error('[Chat] startChatWithUser error:', e);
      setError(e?.message || 'Failed to start chat');
    }
  }, [createOrGetPrivateConversation, fetchConversations]);

  return (
    <div className="chat-page">
      <FloatingSidebarButton userProfile={userProfile} />
      <button
        className="pages-back-btn"
        onClick={() => navigate(-1)}
        aria-label={t.common?.back || "Back"}
        title={t.common?.back || "Back"}
      >
        <FiArrowLeft />
      </button>

      <div className="chat-shell">
        <aside className="chat-sidebar">
          <div className="chat-sidebar-header">
            <div className="chat-title">{t.chat?.title || 'Chat'}</div>
            <button className="chat-refresh-btn" onClick={fetchConversations} disabled={loadingConversations}>
              {t.common?.refresh || 'Refresh'}
            </button>
          </div>

          <div className="chat-search">
            <input
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder={t.chat?.searchUsers || 'Search users...'}
              className="chat-search-input"
            />
            {(searchingUsers || userResults.length > 0) && (
              <div className="chat-search-results">
                {searchingUsers && <div className="chat-search-hint">{t.common?.loading || 'Loading...'}</div>}
                {!searchingUsers && userResults.length === 0 && (
                  <div className="chat-search-hint">{t.common?.noResults || 'No results'}</div>
                )}
                {!searchingUsers && userResults.map((u) => (
                  <button
                    key={u.user_id}
                    className="chat-search-item"
                    onClick={() => startChatWithUser(u.user_id)}
                  >
                    <img
                      className="chat-avatar"
                      src={u.avatar_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K'}
                      alt=""
                    />
                    <div className="chat-search-item-text">
                      <div className="chat-search-name">{safeName(u)}</div>
                      <div className="chat-search-username">@{u.username || u.user_id.slice(0, 8)}</div>
                    </div>
                    <div className="chat-search-action">{t.chat?.start || 'Start'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="chat-conv-list">
            {loadingConversations && <div className="chat-muted">{t.common?.loading || 'Loading...'}</div>}
            {!loadingConversations && conversations.length === 0 && (
              <div className="chat-muted">{t.chat?.noConversations || 'No conversations yet. Search a user to start.'}</div>
            )}

            {!loadingConversations && conversations.map((c) => {
              const isActive = c.id === selectedConversationId;
              const other = c.otherParticipant;
              const title = c.type === 'private' ? safeName(other) : (c.name || 'Conversation');
              const preview = c.lastMessage?.content || (c.lastMessage?.attachment_type ? `[${c.lastMessage.attachment_type}]` : '');
              return (
                <button
                  key={c.id}
                  className={`chat-conv-item ${isActive ? 'active' : ''}`}
                  onClick={() => onSelectConversation(c.id)}
                >
                  <img
                    className="chat-avatar"
                    src={other?.avatar_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K'}
                    alt=""
                  />
                  <div className="chat-conv-meta">
                    <div className="chat-conv-top">
                      <div className="chat-conv-title">{title}</div>
                      {!!c.unreadCount && <div className="chat-unread">{c.unreadCount}</div>}
                    </div>
                    <div className="chat-conv-preview">{preview || (t.chat?.noMessages || 'No messages yet')}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <main className="chat-main">
          {!currentUser?.id && (
            <div className="chat-empty">{t.common?.loading || 'Loading...'}</div>
          )}

          {currentUser?.id && !selectedConversationId && (
            <div className="chat-empty">{t.chat?.pickConversation || 'Pick a conversation or search a user to start.'}</div>
          )}

          {currentUser?.id && selectedConversationId && (
            <>
              <div className="chat-main-header">
                <div
                  className="chat-peer"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!selectedOther?.user_id) return;
                    navigate(`/profile/${selectedOther.user_id}`);
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && selectedOther?.user_id) {
                      e.preventDefault();
                      navigate(`/profile/${selectedOther.user_id}`);
                    }
                  }}
                  title={t.profile?.viewProfile || 'View profile'}
                >
                  <img
                    className="chat-avatar chat-avatar-lg"
                    src={selectedOther?.avatar_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K'}
                    alt=""
                  />
                  <div className="chat-peer-meta">
                    <div className="chat-peer-name">{safeName(selectedOther)}</div>
                    <div className="chat-peer-sub">{selectedOther?.username ? `@${selectedOther.username}` : ''}</div>
                  </div>
                </div>
                {loadingMessages && <div className="chat-muted">{t.common?.loading || 'Loading...'}</div>}
              </div>

              {error && <div className="chat-error">{error}</div>}

              <div className="chat-messages" ref={messagesContainerRef} onScroll={onMessagesScroll}>
                {loadingOlder && <div className="chat-muted">{t.common?.loading || 'Loading...'} {t.chat?.older || 'older messages'}...</div>}
                {messages.map((m) => {
                  const mine = m.sender_id === currentUser.id;
                  const senderName = mine ? (t.chat?.you || 'You') : safeName(m.sender);
                  return (
                    <div key={m.id} className={`chat-bubble-row ${mine ? 'mine' : 'theirs'}`}>
                      <div className={`chat-bubble ${mine ? 'mine' : 'theirs'}`}>
                        <div className="chat-bubble-sender">{senderName}</div>
                        <div className="chat-bubble-text">{m.content || ''}</div>
                        <div className="chat-bubble-time">
                          {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messageEndRef} />
              </div>

              <div className="chat-input-bar">
                <input
                  className="chat-input"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder={t.chat?.typeMessage || 'Type a message...'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <button className="chat-send-btn" onClick={sendMessage} disabled={!messageText.trim()}>
                  {t.chat?.send || 'Send'}
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Chat;
